import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Chip,
  makeStyles,
  Snackbar,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { useTranslation } from "../hooks/useTranslation";
import { getApiBaseUrl } from "../config";

const useStyles = makeStyles((theme) => ({
  cellsContainer: {
    border: "1px solid var(--ikaro-gray-medium)",
    borderRadius: "8px",
    padding: theme.spacing(1),
    marginTop: theme.spacing(2),
    minHeight: 120,
  },
  cellItem: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0.5, 1),
    "&:hover": {
      backgroundColor: "var(--ikaro-gray-light)",
    },
  },
  selectedCellsInfo: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1),
    backgroundColor: "var(--ikaro-gray-light)",
    borderRadius: "4px",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100px",
  },
}));

const UserCellsManager = ({
  userId,
  token,
  onCellsChange,
  initialSelectedCells = [],
  disabled = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation("jobs");
  const [cells, setCells] = useState([]);
  const [selectedCells, setSelectedCells] = useState(initialSelectedCells);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Estados para notificações
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const API_BASE_URL = getApiBaseUrl();

  // Função para mostrar notificações
  const showNotification = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Função para fechar notificação
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  // Associar usuário a célula
  const associateUserCell = async (cellId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/user_cells/associate`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: userId,
          cell_id: cellId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Erro ao associar célula: ${response.status}`);
      }
      showNotification("Célula associada com sucesso!", "success");
    } catch (err) {
      console.error("Erro ao associar célula:", err);
      showNotification("Erro ao associar célula", "error");
      throw err;
    }
  };

  // Desassociar usuário de célula
  const disassociateUserCell = async (cellId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/user_cells/disassociate`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: userId,
          cell_id: cellId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Erro ao desassociar célula: ${response.status}`);
      }
      showNotification("Célula desassociada com sucesso!", "success");
    } catch (err) {
      console.error("Erro ao desassociar célula:", err);
      showNotification("Erro ao desassociar célula", "error");
      throw err;
    }
  };

  // Lidar com mudança de seleção de célula
  const handleCellToggle = async (cellId, checked) => {
    if (isUpdating) return; // Evitar múltiplas chamadas simultâneas

    setIsUpdating(true);
    try {
      if (checked) {
        await associateUserCell(cellId);
        setSelectedCells((prev) => [...prev, cellId]);
      } else {
        await disassociateUserCell(cellId);
        setSelectedCells((prev) => prev.filter((id) => id !== cellId));
      }

      // Notificar componente pai sobre mudanças
      if (onCellsChange) {
        const newSelectedCells = checked
          ? [...selectedCells, cellId]
          : selectedCells.filter((id) => id !== cellId);
        onCellsChange(newSelectedCells);
      }
    } catch (err) {
      console.error("Erro ao atualizar célula:", err);
      setError("Erro ao atualizar célula. Tente novamente.");
    } finally {
      setIsUpdating(false);
    }
  };

  // Verificar se célula está selecionada
  const isCellSelected = (cellId) => selectedCells.includes(cellId);

  // Carregar dados iniciais - apenas uma vez
  useEffect(() => {
    if (userId) {
      const loadData = async () => {
        setLoading(true);
        setError(null);

        try {
          // Timeout para evitar travamento
          const timeoutId = setTimeout(() => {
            setError("Timeout: Carregamento demorou muito. Tente novamente.");
            setLoading(false);
          }, 10000); // 10 segundos timeout

          // Carregar células do tenant do usuário selecionado
          const cellsResponse = await fetch(
            `${API_BASE_URL}/cells/by_user/${userId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

          if (cellsResponse.ok) {
            const cellsData = await cellsResponse.json();
            setCells(cellsData.data || []);
          } else {
            throw new Error(`Erro ao buscar células: ${cellsResponse.status}`);
          }

          // Carregar células do usuário
          const userCellsResponse = await fetch(
            `${API_BASE_URL}/user_cells/by_user/${userId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

          if (userCellsResponse.ok) {
            const userCellsData = await userCellsResponse.json();
            const userCellIds = userCellsData.cells.map((cell) => cell.cell_id);
            setSelectedCells(userCellIds);
          }

          clearTimeout(timeoutId);
          setDataLoaded(true);
        } catch (err) {
          setError(err.message);
          console.error("Erro ao carregar dados:", err);
        } finally {
          setLoading(false);
        }
      };

      loadData();
    }
  }, [userId, token, API_BASE_URL]);

  if (loading && !dataLoaded) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
        <Typography variant="body2" style={{ marginTop: 8 }}>
          Carregando células...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" style={{ marginTop: 16 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t("table.userCells")}
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        {t("table.userCellsDescription")}
      </Typography>

      <div className={classes.cellsContainer}>
        {cells.map((cell) => (
          <div key={cell.id} className={classes.cellItem}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isCellSelected(cell.id)}
                  onChange={(e) => handleCellToggle(cell.id, e.target.checked)}
                  color="primary"
                  disabled={disabled || isUpdating}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">{cell.name}</Typography>
                  {cell.description && (
                    <Typography variant="body2" color="textSecondary">
                      {cell.description}
                    </Typography>
                  )}
                </Box>
              }
            />
          </div>
        ))}
      </div>

      {selectedCells.length > 0 && (
        <div className={classes.selectedCellsInfo}>
          <Typography variant="subtitle2" gutterBottom>
            {t("table.selectedCells")} ({selectedCells.length}):
          </Typography>
          {selectedCells.map((cellId) => {
            const cell = cells.find((c) => c.id === cellId);
            return (
              <Chip
                key={cellId}
                label={cell?.name || `Célula ${cellId}`}
                size="small"
                style={{ margin: 2 }}
              />
            );
          })}
        </div>
      )}

      {/* Snackbar para notificações */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserCellsManager;
